import {Component, DestroyRef, effect, inject, Input, OnInit, signal, untracked} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {GameTableCardComponent} from './game-table-card/game-table-card.component';
import {GameTable} from '../../../../shared/model/game-table';
import {ApiResponse} from '../../../../shared/model/api-response';
import {GameTablesService} from '../../services/gametables.service';
import {ActivatedRoute} from '@angular/router';
import {PaginatedListComponent} from '../../../../shared/components/paginated-list/paginated-list.component';
import {PermissionService} from '../../../../core/services/permission.service';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {sortEditableFirst, SortCompare} from '../../../../shared/utils/editable-first';
import {OsmMapComponent} from '../../../../shared/components/osm-map/osm-map.component';
import {
  countMappableTables,
  tableCoordinates,
  tableMapMarkers,
} from '../../../../shared/utils/map-markers';
import {UserGeolocationService} from '../../../../shared/services/user-geolocation.service';
import {MapMarker} from '../../../../shared/model/map-marker';
import {compareByDistanceKm} from '../../../../shared/utils/geo-distance';
import {
  matchesOrganizationName,
  matchesTableGlobalSearch,
  parseMaxDistanceKm,
  withinMaxDistanceKm,
} from '../../../../shared/utils/catalog-filters';
import {
  GameTableAdvancedFilterComponent,
  GameTableAdvancedFilterValues,
} from './game-table-advanced-filter/game-table-advanced-filter.component';

@Component({
  selector: 'app-game-table-list',
  standalone: true,
  imports: [
    GameTableCardComponent,
    PaginatedListComponent,
    ReactiveFormsModule,
    OsmMapComponent,
    GameTableAdvancedFilterComponent,
  ],
  templateUrl: './game-table-list.component.html',
  styleUrl: './game-table-list.component.css',
})
export class GameTableListComponent implements OnInit {
  private gameTablesService: GameTablesService = inject(GameTablesService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private permissionService: PermissionService = inject(PermissionService);
  private userGeolocation: UserGeolocationService = inject(UserGeolocationService);
  private destroyRef = inject(DestroyRef);

  protected isMineTables = false;
  private hasExternalGameTables = false;
  protected allGameTables: Array<GameTable> = [];
  private _gameTables: Array<GameTable> = [];
  protected view = signal<'list' | 'map'>('list');

  protected isAdvancedSearch = signal<boolean>(false);
  protected includePast = signal<boolean>(false);
  protected isLoading = signal<boolean>(true);
  protected searchControl = new FormControl<string>('', {nonNullable: true});
  protected advancedFilters = signal<GameTableAdvancedFilterValues>({
    tableName: null,
    gameName: null,
    eventName: null,
    location: null,
    organization: null,
    maxDistanceKm: null,
  });

  @Input() showTitle: boolean = false;
  @Input() showDescription: boolean = true;

  @Input()
  set gameTables(value: Array<GameTable> | null | undefined) {
    if (value === undefined) {
      this.hasExternalGameTables = false;
      return;
    }

    this.hasExternalGameTables = true;
    this.isLoading.set(false);
    this.allGameTables = value ?? [];
    this.applyLocalFilters();
  }

  get gameTables(): Array<GameTable> {
    return this._gameTables;
  }

  constructor() {
    effect(() => {
      this.userGeolocation.position();
      untracked(() => this.resortDisplayedTables());
    });
  }

  ngOnInit(): void {
    this.userGeolocation.ensureLocated();

    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyLocalFilters());

    if (!this.hasExternalGameTables) {
      this.isMineTables = this.route.snapshot.data['filter'];
      this.loadTables();
    }
  }

  protected setView(view: 'list' | 'map'): void {
    this.view.set(view);
  }

  protected mapMarkers(): MapMarker[] {
    return tableMapMarkers(this._gameTables);
  }

  protected mappableCount(): number {
    return countMappableTables(this._gameTables);
  }

  protected loadTables(): void {
    if (this.isMineTables) {
      this.loadMyTables();
    } else {
      this.loadAllTables();
    }
  }

  protected loadAllTables(): void {
    this.isLoading.set(true);
    this.gameTablesService
      .getAllTables(this.includePast())
      .subscribe({
        next: (response: ApiResponse<GameTable[]>) => {
          if (response.success && response.data) {
            this.allGameTables = response.data;
            this.applyLocalFilters();
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        }
      });
  }

  private loadMyTables(): void {
    this.isLoading.set(true);
    this.gameTablesService.getMineTables().subscribe({
      next: (response: ApiResponse<GameTable[]>) => {
        if (response.success && response.data) {
          this.allGameTables = response.data;
          this._gameTables = this.sortTables(response.data);
        }
        this.isLoading.set(false);
      },
      error: () => {
          this.isLoading.set(false);
        }
      });
  }

  isLogged(): boolean {
    return this.permissionService.isLogged();
  }

  protected toggleAdvancedSearch(): void {
    this.isAdvancedSearch.update((current) => !current);
    this.applyLocalFilters();
  }

  protected closeAdvancedSearch(): void {
    this.isAdvancedSearch.set(false);
    this.applyLocalFilters();
  }

  protected clearSearch(): void {
    this.searchControl.setValue('');
  }

  protected onAdvancedFilterChange(values: GameTableAdvancedFilterValues): void {
    this.advancedFilters.set(values);
    this.applyLocalFilters();
  }

  protected onIncludePastChange(includePast: boolean): void {
    this.includePast.set(includePast);
    if (!this.isMineTables && !this.hasExternalGameTables) {
      this.loadAllTables();
    }
  }

  protected onAdvancedFilterReset(): void {
    this.advancedFilters.set({
      tableName: null,
      gameName: null,
      eventName: null,
      location: null,
      organization: null,
      maxDistanceKm: null,
    });
    if (this.includePast()) {
      this.includePast.set(false);
      if (!this.isMineTables && !this.hasExternalGameTables) {
        this.loadAllTables();
        return;
      }
    }
    this.applyLocalFilters();
  }

  protected toggleIncludePast(): void {
    this.includePast.update((prev) => !prev);
    if (!this.isMineTables && !this.hasExternalGameTables) {
      this.loadAllTables();
    }
  }

  protected applyLocalFilters(): void {
    if (this.isMineTables) {
      this._gameTables = this.sortTables(this.allGameTables);
      return;
    }

    if (this.isAdvancedSearch()) {
      const filters = this.advancedFilters();
      const tableNameFilter = (filters.tableName ?? '').toLowerCase().trim();
      const gameNameFilter = (filters.gameName ?? '').toLowerCase().trim();
      const eventNameFilter = (filters.eventName ?? '').toLowerCase().trim();
      const locationFilter = (filters.location ?? '').toLowerCase().trim();
      const organizationFilter = filters.organization;
      const maxKm = parseMaxDistanceKm(filters.maxDistanceKm);
      const user = this.userGeolocation.position();

      this._gameTables = this.sortTables(
        this.allGameTables.filter((table) => {
          const tableName = (table.tableName ?? '').toLowerCase();
          const gameName = (table.playedGame?.gameName ?? '').toLowerCase();
          const eventName = (table.event?.name ?? '').toLowerCase();
          const locationName = this.extractLocationName(table).toLowerCase();
          const organizationName =
            table.organization?.organizationName ?? table.event?.organization?.organizationName;

          return (
            (!tableNameFilter || tableName.includes(tableNameFilter)) &&
            (!gameNameFilter || gameName.includes(gameNameFilter)) &&
            (!eventNameFilter || eventName.includes(eventNameFilter)) &&
            (!locationFilter || locationName.includes(locationFilter)) &&
            matchesOrganizationName(organizationName, organizationFilter) &&
            withinMaxDistanceKm(user, tableCoordinates(table), maxKm)
          );
        }),
      );
    } else {
      const query = this.searchControl.value;
      this._gameTables = this.sortTables(
        this.allGameTables.filter((table) => matchesTableGlobalSearch(table, query)),
      );
    }
  }

  private resortDisplayedTables(): void {
    if (this.hasExternalGameTables) {
      this._gameTables = this.sortTables(this._gameTables);
      return;
    }
    if (this.isMineTables) {
      this._gameTables = this.sortTables(this._gameTables);
      return;
    }
    if (this.allGameTables.length > 0) {
      this.applyLocalFilters();
    }
  }

  private sortTables(listed: GameTable[]): GameTable[] {
    const thenBy = this.secondarySort();
    if (this.isMineTables) {
      return sortEditableFirst(listed, (table) => table.isCreator, thenBy);
    }
    return [...listed].sort(thenBy);
  }

  private secondarySort(): SortCompare<GameTable> {
    const byCreatedThenId = (first: GameTable, second: GameTable) => {
      const secondTime = Date.parse(second.createdDate ?? '') || 0;
      const firstTime = Date.parse(first.createdDate ?? '') || 0;
      if (secondTime !== firstTime) {
        return secondTime - firstTime;
      }
      return (second.id ?? 0) - (first.id ?? 0);
    };
    const user = this.userGeolocation.position();
    if (!user) {
      return byCreatedThenId;
    }
    return (first, second) => {
      const byDistance = compareByDistanceKm(
        user,
        tableCoordinates(first),
        tableCoordinates(second),
      );
      return byDistance !== 0 ? byDistance : byCreatedThenId(first, second);
    };
  }

  private extractLocationName(table: GameTable): string {
    return (
      table.location?.formalName ??
      table.location?.displayName ??
      table.location?.city ??
      table.location?.town ??
      table.location?.village ??
      'Online'
    );
  }
}
